import { pageTitle } from '@palaccount/config';
import {
  LEAD_DISQUALIFY_REASONS,
  LEAD_SOURCES,
  type LeadDisqualifyReason,
  type LeadResponse,
  type LeadSource,
  type LeadStatus,
} from '@palaccount/contracts';
import { ArrowRight, CalendarX2, Search, TrendingUp, X, Zap } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Ltr, Pct } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Modal } from '@/features/inventory/modal';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useCan } from '@/lib/permissions';
import { localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import {
  useAdvanceLead,
  useConvertLead,
  useCreateLead,
  useDisqualifyLead,
  useLead,
  useLeads,
  useLeadStats,
  useLogLeadActivity,
} from './api';
import { CrmTabs } from './crm-tabs';
import { TaskPanel } from './task-panel';

/**
 * CRM-01 — Leads. One screen for every user (owner ruling R1): the metric
 * strip, the overdue banner, board / table / overdue views, Quick capture,
 * the detail card with its append-only activity log, the source performance
 * panel and the embedded follow-up task panel. What differs between users is
 * what they may WRITE — crm.create for capture, crm.edit for advance /
 * convert / disqualify / log — checked on the server where the write happens;
 * the buttons here only mirror that with useCan.
 */

const STATUS_META: Record<LeadStatus, { key: string; chip: string }> = {
  new: { key: 'crm.stNew', chip: 'bg-blue-100 text-blue-800' },
  contacted: { key: 'crm.stContacted', chip: 'bg-amber-100 text-amber-800' },
  qualified: { key: 'crm.stQualified', chip: 'bg-violet-100 text-violet-800' },
  converted: { key: 'crm.stConverted', chip: 'bg-emerald-100 text-emerald-800' },
  disqualified: { key: 'crm.stDisqualified', chip: 'bg-muted text-muted-foreground' },
};

const SOURCE_KEYS: Record<LeadSource, string> = {
  website: 'crm.srcWebsite',
  referral: 'crm.srcReferral',
  cold_call: 'crm.srcColdCall',
  trade_show: 'crm.srcTradeShow',
  ad_campaign: 'crm.srcAdCampaign',
  walk_in: 'crm.srcWalkIn',
  other: 'crm.srcOther',
};

const REASON_KEYS: Record<LeadDisqualifyReason, string> = {
  not_interested: 'crm.rsNotInterested',
  no_budget: 'crm.rsNoBudget',
  bad_timing: 'crm.rsBadTiming',
  duplicate: 'crm.rsDuplicate',
  other: 'crm.rsOther',
};

/** F-108 / BR-CRM-01-04: 70+ green, 40–69 amber, below 40 grey; 0 a dash.
    The score itself is a stored field, not a calculation. */
function ScorePill({ score }: { score: number }) {
  if (score === 0) return <span className="text-muted-foreground">—</span>;
  const band =
    score >= 70
      ? 'bg-emerald-100 text-emerald-800'
      : score >= 40
        ? 'bg-amber-100 text-amber-800'
        : 'bg-muted text-muted-foreground';
  return (
    <span className={cn('inline-block rounded-full px-2 py-0.5 font-mono text-xs font-bold', band)}>
      {score}
    </span>
  );
}

function StatusChip({ status }: { status: LeadStatus }) {
  const { t } = useTranslation();
  const meta = STATUS_META[status];
  return (
    <span className={cn('inline-block rounded-full px-2 py-0.5 text-xs font-semibold', meta.chip)}>
      {t(meta.key)}
    </span>
  );
}

function FollowUpCell({ lead }: { lead: LeadResponse }) {
  const { t } = useTranslation();
  if (lead.nextFollowUp === null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-2">
      <Ltr
        value={lead.nextFollowUp}
        className={cn(
          'font-mono text-xs',
          lead.overdue && 'font-bold text-red-600 dark:text-red-400',
          lead.dueSoon && 'font-bold text-amber-700 dark:text-amber-400',
        )}
      />
      {lead.overdue && lead.daysOverdue !== null ? (
        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">
          {t('crm.daysOverdue', { count: lead.daysOverdue })}
        </span>
      ) : null}
    </span>
  );
}

/** The table and each board column ask for one block at a time (NFR-42). */
const TABLE_PAGE_SIZE = 25;
const BOARD_PAGE_SIZE = 25;

/**
 * ONE board column: the same paged route as the table, carrying only its own
 * status.
 *
 * It is a component rather than a `.map()` body because each column is its own
 * query and its own page, and a hook cannot be called inside a loop. The
 * column header prints the SERVER's total for that status, so the number a
 * planner reads is the count of the status and not the count of the page.
 */
function BoardColumn({
  status,
  selectedId,
  canEdit,
  onSelect,
  onConvert,
  onDisqualify,
}: {
  status: LeadStatus;
  selectedId: string | undefined;
  canEdit: boolean;
  onSelect: (id: string) => void;
  onConvert: (lead: LeadResponse) => void;
  onDisqualify: (lead: LeadResponse) => void;
}) {
  const { t } = useTranslation();
  const paging = usePagedState(BOARD_PAGE_SIZE, `board-${status}`);
  const column = useLeads({ status }, paging.page, BOARD_PAGE_SIZE);
  const range = usePageRange(column.data, BOARD_PAGE_SIZE);

  return (
    <div className="w-[266px] shrink-0 rounded-[14px] bg-muted/50 p-2">
      <div className="mb-2 flex items-center justify-between px-1">
        <StatusChip status={status} />
        <span className="font-mono text-xs text-muted-foreground">{range.total}</span>
      </div>
      <div className="grid gap-2">
        {(column.data?.leads ?? []).map((lead) => (
          <div
            key={lead.id}
            className={cn(
              'rounded-md border bg-background p-2 text-sm shadow-sm',
              lead.overdue && 'border-s-4 border-s-red-500 bg-red-50/50',
              lead.dueSoon && 'border-s-4 border-s-amber-500',
              selectedId === lead.id && 'ring-2 ring-primary',
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(lead.id)}
              className="w-full text-start"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold">{lead.name}</span>
                <ScorePill score={lead.score} />
              </div>
              {lead.company ? (
                <div className="text-xs text-muted-foreground">{lead.company}</div>
              ) : null}
              <div className="mt-1 flex items-center justify-between gap-2">
                <Ltr value={lead.code} className="font-mono text-[11px] text-muted-foreground" />
                <FollowUpCell lead={lead} />
              </div>
              {lead.status === 'converted' && lead.convertedOppCode ? (
                <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                  <TrendingUp className="size-3" aria-hidden />
                  <Ltr value={lead.convertedOppCode} className="font-mono" />
                </div>
              ) : null}
            </button>
            {/* BR-CRM-01-08 / BR-CRM-01-09 / ERR-CRM-01-05: the board's route
                to Disqualified or Converted opens the MATCHING MODAL and
                changes nothing — the prototype's gesture was a card drag;
                this board's affordance is the card's own buttons, exactly as
                the CRM-02 board answered the same rule for Won and Lost
                (crm02-error-rules.test.tsx). Convert and Disqualify here only
                set the modal's target, the sole write is each modal's own
                Confirm, and the "quick move" ERR-CRM-01-05's user expected
                has no code path at all: the leads contract carries no
                set-status route — `advance` can never reach a terminal status
                (BR-CRM-01-07) and only convert / disqualify can end a lead.
                A finished lead renders no buttons (§11: "blocked … because
                their buttons are not rendered"). */}
            {lead.status !== 'converted' && lead.status !== 'disqualified' ? (
              <div className="mt-1 flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canEdit}
                  title={canEdit ? undefined : t('crm.noStatusPermission')}
                  onClick={() => onConvert(lead)}
                >
                  {t('crm.convert')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 dark:text-red-400"
                  disabled={!canEdit}
                  title={canEdit ? undefined : t('crm.noStatusPermission')}
                  onClick={() => onDisqualify(lead)}
                >
                  {t('crm.disqualify')}
                </Button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {range.pages > 1 ? (
        <div className="mt-2">
          <Pager
            page={paging.page}
            pages={range.pages}
            first={range.first}
            last={range.last}
            total={range.total}
            onPage={paging.setPage}
            label={t(STATUS_META[status].key)}
          />
        </div>
      ) : null}
    </div>
  );
}

export function LeadsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canEdit = useCan('crm.edit');

  const [view, setView] = useState<'board' | 'table' | 'overdue'>('board');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | LeadStatus>('');
  const [sourceFilter, setSourceFilter] = useState<'' | LeadSource>('');
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [convertTarget, setConvertTarget] = useState<LeadResponse | null>(null);
  const [disqualifyTarget, setDisqualifyTarget] = useState<LeadResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = pageTitle(t('crm.leadsTitle'));
  }, [t]);

  /*
   * ── NFR-42 ON A SCREEN THAT WAS THREE READS OF ONE ARRAY ──────────────────
   *
   * This page used to fetch every lead in the tenant once and derive
   * everything from it in the browser: the metric strip, the five board
   * columns, the filtered table and §5d's source performance. That is
   * PAGINATION.md §3 step 10's case — a page would have turned every figure on
   * the strip into a count of the page.
   *
   * So the strip and §5d come from `/api/crm/leads/stats`, which aggregates
   * over EVERY lead, and the rows come from the paged list: the table asks
   * with the filters, and each board column asks with its own `status`. The
   * board is still unfiltered (BR-CRM-01-15) — a column carries no `q`, no
   * source and no status beyond its own — it is simply drawn a page at a time.
   */
  const tablePaging = usePagedState(TABLE_PAGE_SIZE, 'table');
  const tableQuery = useMemo(
    () => ({
      ...(search.trim() === '' ? {} : { q: search.trim() }),
      ...(statusFilter === '' ? {} : { status: statusFilter }),
      ...(sourceFilter === '' ? {} : { source: sourceFilter }),
      ...(view === 'overdue' ? { overdue: 'true' as const } : {}),
    }),
    [search, statusFilter, sourceFilter, view],
  );
  const table = useLeads(tableQuery, tablePaging.page, TABLE_PAGE_SIZE);
  const tableRange = usePageRange(table.data, TABLE_PAGE_SIZE);

  const { data: stats } = useLeadStats();
  const { data: selected } = useLead(selectedId);
  const advance = useAdvanceLead();
  const convert = useConvertLead();
  const disqualify = useDisqualifyLead();
  const logActivity = useLogLeadActivity();

  const metrics = {
    newCount: stats?.byStatus.new ?? 0,
    contacted: stats?.byStatus.contacted ?? 0,
    qualified: stats?.byStatus.qualified ?? 0,
    overdue: stats?.overdue ?? 0,
    converted: stats?.byStatus.converted ?? 0,
  };

  const filtered = table.data?.leads ?? [];

  // 5d — Source performance: bar = VOLUME share, number = conversion rate;
  // sources with no leads are dropped entirely. The counts are the server's
  // aggregate over every lead; the two ratios are still arithmetic on them.
  const sourceRows = useMemo(() => {
    const counted = new Map((stats?.sources ?? []).map((row) => [row.source, row]));
    const rows = LEAD_SOURCES.map((source) => {
      const of = counted.get(source);
      const count = of?.count ?? 0;
      return {
        source,
        count,
        rate: count === 0 ? 0 : Math.round(((of?.converted ?? 0) / count) * 100),
      };
    }).filter((row) => row.count > 0);
    rows.sort((a, b) => b.count - a.count);
    const max = rows[0]?.count ?? 1;
    return rows.map((row) => ({ ...row, share: Math.round((row.count / max) * 100) }));
  }, [stats]);

  const act = (promise: Promise<unknown>) => {
    setError('');
    promise.catch((cause: unknown) => {
      // The cause reaches the decision — a refusal keeps the server's sentence
      // localized, a request that never answered gets this screen's own rather
      // than the browser's `Failed to fetch`. `crm.errSaveFailedLead` is the
      // leads screen's alone; follow-ups and opportunities have their own.
      setError(localizeServerCause(t, cause, 'crm.errSaveFailedLead'));
    });
  };

  const onConvert = (lead: LeadResponse) => {
    act(
      convert.mutateAsync(lead.id).then((result) => {
        setConvertTarget(null);
        // BR-CRM-01-14: jump straight to the new deal.
        navigate(`/partners/opportunities?selected=${result.opportunityId}`);
      }),
    );
  };

  const boardColumns: LeadStatus[] = ['new', 'contacted', 'qualified', 'converted', 'disqualified'];

  return (
    <div className="grid gap-4">
      <CrmTabs />
      <div className="flex flex-wrap items-center gap-3">
        <div className="me-auto">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {t('crm.eyebrow')}
          </div>
          <h2 className="text-lg font-bold">{t('crm.leadsTitle')}</h2>
        </div>
        <Gated permission="crm.create" reasonKey="perm.cannot.createLeads">
          <Button onClick={() => setCaptureOpen((open) => !open)}>
            <Zap aria-hidden /> {t('crm.newLead')}
          </Button>
        </Gated>
      </div>

      {error ? (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {/* The metric strip. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {(
          [
            ['crm.mNew', metrics.newCount, false],
            ['crm.mContacted', metrics.contacted, false],
            ['crm.mQualified', metrics.qualified, false],
            ['crm.mOverdue', metrics.overdue, metrics.overdue > 0],
            ['crm.mConverted', metrics.converted, false],
          ] as const
        ).map(([key, count, alert]) => (
          <Card key={key} className={cn(alert && 'border-red-400')}>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">{t(key)}</div>
              <div className={cn('font-mono text-2xl font-bold', alert && 'text-red-600 dark:text-red-400')}>
                {count}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* BR-CRM-01-17: the overdue banner, only when the count is above zero. */}
      {metrics.overdue > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-red-300 bg-red-50 px-4 py-3">
          <CalendarX2 className="size-6 text-red-600 dark:text-red-400" aria-hidden />
          <div className="me-auto">
            <div className="text-sm font-bold text-red-700 dark:text-red-400">
              {t('crm.overdueBanner', { count: metrics.overdue })}
            </div>
            <div className="text-xs text-red-600 dark:text-red-400">{t('crm.overdueBannerSub')}</div>
          </div>
          <Button variant="destructive" size="sm" onClick={() => setView('overdue')}>
            {t('crm.viewOverdue')} <ArrowRight aria-hidden />
          </Button>
        </div>
      ) : null}

      {/* Toolbar: views + search. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="me-auto flex items-center gap-1">
          {(['board', 'table'] as const).map((name) => (
            <Button
              key={name}
              size="sm"
              variant={view === name ? 'default' : 'outline'}
              onClick={() => tablePaging.change(setView)(name)}
            >
              {t(name === 'board' ? 'crm.viewBoard' : 'crm.viewTable')}
            </Button>
          ))}
          <Button
            size="sm"
            variant={view === 'overdue' ? 'destructive' : 'outline'}
            className={cn(view !== 'overdue' && 'border-red-300 text-red-600 dark:text-red-400')}
            onClick={() => tablePaging.change(setView)('overdue')}
          >
            {t('crm.viewOverdueTab', { count: metrics.overdue })}
          </Button>
        </div>
        <div className="relative w-full max-w-[250px]">
          <Search
            className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            aria-label={t('crm.searchLeads')}
            placeholder={t('crm.searchLeadsPh')}
            className="ps-9"
            value={search}
            // Through the SETTER, never an effect (BR-DSH-09-19): changing a
            // filter returns to page 1, and an effect would fire on the first
            // render and throw away the page the URL just asked for.
            onChange={(event) => tablePaging.change(setSearch)(event.target.value)}
          />
        </div>
      </div>

      {captureOpen ? (
        <QuickCapture
          onClose={() => setCaptureOpen(false)}
          onCreated={(id) => {
            setCaptureOpen(false);
            setSelectedId(id);
          }}
        />
      ) : null}

      {/* Filters (table only — BR-CRM-01-15). */}
      <div className="flex flex-wrap items-center gap-3">
        <NativeSelect
          aria-label={t('crm.filterSource')}
          value={sourceFilter}
          onChange={(event) =>
            tablePaging.change(setSourceFilter)(event.target.value as '' | LeadSource)
          }
          className="w-auto"
        >
          <option value="">{t('crm.allSources')}</option>
          {LEAD_SOURCES.map((source) => (
            <option key={source} value={source}>
              {t(SOURCE_KEYS[source])}
            </option>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label={t('crm.filterStatus')}
          value={statusFilter}
          onChange={(event) =>
            tablePaging.change(setStatusFilter)(event.target.value as '' | LeadStatus)
          }
          className="w-auto"
        >
          <option value="">{t('crm.allStatuses')}</option>
          {(Object.keys(STATUS_META) as LeadStatus[]).map((status) => (
            <option key={status} value={status}>
              {t(STATUS_META[status].key)}
            </option>
          ))}
        </NativeSelect>
        <span className="ms-auto text-sm text-muted-foreground">
          {t('crm.leadCount', { count: tableRange.total })}
        </span>
      </div>

      {/* Board — unfiltered, always every status (BR-CRM-01-15). Each column
          is its own paged read of the same route, carrying only its own
          `status`: the board still shows every kind of lead, it just draws
          them a page at a time and states each column's real total. */}
      {view === 'board' ? (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {boardColumns.map((status) => (
            <BoardColumn
              key={status}
              status={status}
              selectedId={selectedId}
              canEdit={canEdit}
              onSelect={setSelectedId}
              onConvert={setConvertTarget}
              onDisqualify={setDisqualifyTarget}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto">
            <Table className="min-w-[820px]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('crm.colCode')}</TableHead>
                  <TableHead>{t('crm.colName')}</TableHead>
                  <TableHead>{t('crm.colSource')}</TableHead>
                  <TableHead>{t('crm.colSalesperson')}</TableHead>
                  <TableHead>{t('crm.colStatus')}</TableHead>
                  <TableHead>{t('crm.colScore')}</TableHead>
                  <TableHead>{t('crm.colFollowUp')}</TableHead>
                  <TableHead>{t('crm.colActions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* ERR-CRM-01-06 — a search with no matches. The §14 row says
                    the prototype left "the table body empty", which is the
                    defect, not the target: the defects table names the same
                    thing again ("**No leads match your filters.** appears
                    exactly when there ARE rows, and never when there are
                    none"). So the condition below is the fix in both
                    directions — the message appears when, and only when, the
                    filtered count is zero — and its text carries the row's own
                    remedy, "clear the search or the filters". */}
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                      {t('crm.noLeads')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((lead) => (
                    <TableRow
                      key={lead.id}
                      onClick={() => setSelectedId(lead.id)}
                      className={cn('cursor-pointer', lead.overdue && 'bg-red-50/60')}
                    >
                      <TableCell>
                        <Ltr value={lead.code} className="font-mono text-muted-foreground" />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{lead.name}</div>
                        {lead.company ? (
                          <div className="text-xs text-muted-foreground">{lead.company}</div>
                        ) : null}
                      </TableCell>
                      <TableCell>{t(SOURCE_KEYS[lead.source])}</TableCell>
                      <TableCell>
                        {lead.salespersonName ?? (
                          <span className="text-muted-foreground">{t('crm.unassigned')}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={lead.status} />
                      </TableCell>
                      <TableCell>
                        <ScorePill score={lead.score} />
                      </TableCell>
                      <TableCell>
                        <FollowUpCell lead={lead} />
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        {lead.status === 'new' || lead.status === 'contacted' ? (
                          // BR-CRM-01-18: Advance lives in the table too.
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!canEdit}
                            title={canEdit ? undefined : t('crm.noStatusPermission')}
                            onClick={() => act(advance.mutateAsync(lead.id))}
                          >
                            {t('crm.advance')}
                          </Button>
                        ) : null}
                        {lead.status !== 'converted' && lead.status !== 'disqualified' ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              className="ms-1"
                              disabled={!canEdit}
                              title={canEdit ? undefined : t('crm.noStatusPermission')}
                              onClick={() => setConvertTarget(lead)}
                            >
                              {t('crm.convert')}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="ms-1 text-red-600 dark:text-red-400"
                              disabled={!canEdit}
                              title={canEdit ? undefined : t('crm.noStatusPermission')}
                              onClick={() => setDisqualifyTarget(lead)}
                            >
                              {t('crm.disqualify')}
                            </Button>
                          </>
                        ) : lead.convertedOppCode ? (
                          <Ltr value={lead.convertedOppCode} className="font-mono text-xs text-emerald-700 dark:text-emerald-400" />
                        ) : lead.disqualifyReason ? (
                          <span className="text-xs text-muted-foreground">
                            {t(REASON_KEYS[lead.disqualifyReason])}
                          </span>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <Pager
              page={tablePaging.page}
              pages={tableRange.pages}
              first={tableRange.first}
              last={tableRange.last}
              total={tableRange.total}
              onPage={tablePaging.setPage}
              label={t('crm.leadsTitle')}
            />
          </CardContent>
        </Card>
      )}

      {/* Detail + source performance. */}
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardContent className="grid gap-3">
            {selected ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="me-auto text-base font-bold">
                    {selected.name}
                    {selected.company ? (
                      <span className="ms-2 text-sm font-normal text-muted-foreground">
                        {selected.company}
                      </span>
                    ) : null}
                  </h3>
                  <StatusChip status={selected.status} />
                  <ScorePill score={selected.score} />
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  {selected.phone ? <Ltr value={selected.phone} className="font-mono" /> : null}
                  {selected.email ? <Ltr value={selected.email} className="font-mono" /> : null}
                  <span>{t(SOURCE_KEYS[selected.source])}</span>
                  <FollowUpCell lead={selected} />
                </div>
                {selected.status !== 'converted' && selected.status !== 'disqualified' ? (
                  <ActivityComposer
                    disabled={!canEdit}
                    onSubmit={(kind, body) =>
                      act(logActivity.mutateAsync({ leadId: selected.id, body: { kind, body } }))
                    }
                  />
                ) : null}
                <div className="grid gap-2" data-testid="lead-activity-log">
                  {selected.activities.map((entry) => (
                    <div key={entry.id} className="rounded-md border px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-semibold uppercase">{t(`crm.act_${entry.kind}`)}</span>
                        {entry.createdByName ? <span>{entry.createdByName}</span> : null}
                        <Ltr value={entry.createdAt.slice(0, 16).replace('T', ' ')} className="font-mono" />
                      </div>
                      <div>{entry.body}</div>
                    </div>
                  ))}
                </div>
                <TaskPanel relatedKind="lead" relatedId={selected.id} relatedKey={selected.code} relatedName={selected.company ?? selected.name} />
              </>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('crm.selectLead')}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="grid gap-2">
            <h3 className="text-base font-bold">{t('crm.sourcePerf')}</h3>
            <p className="text-xs text-muted-foreground">{t('crm.sourcePerfSub')}</p>
            {sourceRows.map((row) => (
              <div key={row.source} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 text-sm">
                <div>
                  <div className="flex items-center justify-between">
                    <span>{t(SOURCE_KEYS[row.source])}</span>
                  </div>
                  {/* The bar encodes VOLUME, not rate (5d). */}
                  <div className="mt-1 h-1.5 w-full rounded bg-muted">
                    <div className="h-1.5 rounded bg-blue-500" style={{ width: `${row.share}%` }} />
                  </div>
                </div>
                <span className="font-mono text-xs text-muted-foreground">{row.count}</span>
                <span
                  className={cn(
                    'font-mono text-xs font-bold',
                    row.rate >= 40
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : row.rate > 0
                        ? 'text-amber-700 dark:text-amber-400'
                        : 'text-muted-foreground',
                  )}
                >
                  <Pct value={row.rate} />
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Convert modal (BR-CRM-01-13's preview). */}
      {convertTarget ? (
        <Modal title={t('crm.convertTitle')} onClose={() => setConvertTarget(null)}>
          <div className="grid gap-3">
            <p className="text-sm text-muted-foreground">
              {convertTarget.name}
              {convertTarget.company ? ` · ${convertTarget.company}` : ''}
            </p>
            <p className="text-sm">{t('crm.convertExplain')}</p>
            <ul className="grid gap-1 text-sm">
              {[
                t('crm.cvName'),
                t('crm.cvCompany'),
                t('crm.cvContact'),
                t('crm.cvSource'),
                t('crm.cvSalesperson'),
                t('crm.cvLog', { count: convertTarget.activityCount }),
              ].map((line) => (
                <li key={line} className="flex items-center gap-2">
                  <ArrowRight className="size-3 text-emerald-700 dark:text-emerald-400" aria-hidden /> {line}
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConvertTarget(null)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={() => onConvert(convertTarget)} disabled={convert.isPending}>
                {t('crm.convertConfirm')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {/* Disqualify modal — the confirm stays disabled without a reason (BR-CRM-01-10). */}
      {disqualifyTarget ? (
        <DisqualifyModal
          lead={disqualifyTarget}
          onClose={() => setDisqualifyTarget(null)}
          onConfirm={(reason, note) =>
            act(
              disqualify
                .mutateAsync({
                  leadId: disqualifyTarget.id,
                  body: { reason, ...(note ? { note } : {}) },
                })
                .then(() => setDisqualifyTarget(null)),
            )
          }
        />
      ) : null}
    </div>
  );
}

/** 5c — Quick capture: three required answers, Create disabled until present. */
function QuickCapture({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (leadId: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateLead();
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [contactKind, setContactKind] = useState<'phone' | 'email'>('phone');
  const [contact, setContact] = useState('');
  const [source, setSource] = useState<'' | LeadSource>('');
  const [score, setScore] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState('');

  // BR-CRM-01-01: name + one contact + source, or the button stays disabled.
  const complete = name.trim().length > 0 && contact.trim().length > 0 && source !== '';

  const submit = () => {
    if (!complete) return;
    const clamped = Math.max(0, Math.min(100, Number(score) || 0));
    create
      .mutateAsync({
        name: name.trim(),
        ...(company.trim() ? { company: company.trim() } : {}),
        ...(contactKind === 'phone' ? { phone: contact.trim() } : { email: contact.trim() }),
        source: source as LeadSource,
        ...(clamped > 0 ? { score: clamped } : {}),
        ...(followUp ? { nextFollowUp: followUp } : {}),
      })
      .then((result) => {
        setWarnings(result.warnings);
        onCreated(result.lead.id);
      })
      .catch((cause: unknown) => {
        setError(localizeServerCause(t, cause, 'crm.errSaveFailedLead'));
      });
  };

  return (
    <Card className="border-primary">
      <CardContent className="grid gap-3">
      <div className="flex items-center gap-2">
        <Zap className="size-4 text-primary" aria-hidden />
        <h3 className="me-auto text-base font-bold">{t('crm.quickCapture')}</h3>
        <button type="button" aria-label={t('common.close')} onClick={onClose}>
          <X className="size-4" aria-hidden />
        </button>
      </div>
      <p className="text-xs text-muted-foreground">{t('crm.quickCaptureHint')}</p>
      {error ? (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}
      {warnings.map((warning) => (
        <div key={warning} className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {warning}
        </div>
      ))}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1">
          <Label htmlFor="lead-name">
            {t('crm.fName')} <span className="text-red-600 dark:text-red-400">{t('crm.required')}</span>
          </Label>
          <Input id="lead-name" value={name} onChange={(event) => setName(event.target.value)} />
        </div>
        <div className="grid gap-1">
          <Label htmlFor="lead-company">
            {t('crm.fCompany')}{' '}
            <span className="text-muted-foreground">{t('crm.optional')}</span>
          </Label>
          <Input id="lead-company" value={company} onChange={(event) => setCompany(event.target.value)} />
        </div>
      </div>
      <div className="grid gap-3 border-t border-dashed pt-3 sm:grid-cols-2">
        <div className="grid gap-1">
          <Label>
            {t('crm.fContact')} <span className="text-red-600 dark:text-red-400">{t('crm.required')}</span>
          </Label>
          <div className="flex gap-1">
            {(['phone', 'email'] as const).map((kind) => (
              <Button
                key={kind}
                type="button"
                size="sm"
                variant={contactKind === kind ? 'default' : 'outline'}
                onClick={() => setContactKind(kind)}
              >
                {t(kind === 'phone' ? 'crm.fPhone' : 'crm.fEmail')}
              </Button>
            ))}
          </div>
          <Input
            aria-label={t('crm.fContactValue')}
            dir="ltr"
            className="font-mono"
            value={contact}
            onChange={(event) => setContact(event.target.value)}
          />
        </div>
        <div className="grid gap-1">
          <Label htmlFor="lead-source">
            {t('crm.fSource')} <span className="text-red-600 dark:text-red-400">{t('crm.required')}</span>
          </Label>
          <NativeSelect
            id="lead-source"
            value={source}
            onChange={(event) => setSource(event.target.value as '' | LeadSource)}
          >
            <option value="">{t('crm.chooseSource')}</option>
            {LEAD_SOURCES.map((value) => (
              <option key={value} value={value}>
                {t(SOURCE_KEYS[value])}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>
      <div className="grid gap-3 border-t border-dashed pt-3 sm:grid-cols-2">
        <div className="grid gap-1">
          <Label htmlFor="lead-score">
            {t('crm.fScore')} <span className="text-muted-foreground">{t('crm.optional')}</span>
          </Label>
          <Input
            id="lead-score"
            inputMode="numeric"
            placeholder="0–100"
            dir="ltr"
            className="font-mono"
            value={score}
            onChange={(event) => setScore(event.target.value.replace(/\D/g, ''))}
          />
        </div>
        <div className="grid gap-1">
          <Label htmlFor="lead-follow-up">
            {t('crm.fFollowUp')} <span className="text-muted-foreground">{t('crm.optional')}</span>
          </Label>
          <Input
            id="lead-follow-up"
            type="date"
            dir="ltr"
            value={followUp}
            onChange={(event) => setFollowUp(event.target.value)}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button disabled={!complete || create.isPending} onClick={submit}>
          {t('crm.createLead')}
        </Button>
      </div>
      </CardContent>
    </Card>
  );
}

function ActivityComposer({
  disabled,
  onSubmit,
}: {
  disabled: boolean;
  onSubmit: (kind: 'note' | 'call' | 'email' | 'meeting', body: string) => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<'note' | 'call' | 'email' | 'meeting'>('note');
  const [body, setBody] = useState('');
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-1">
        {(['note', 'call', 'email', 'meeting'] as const).map((value) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={kind === value ? 'default' : 'outline'}
            onClick={() => setKind(value)}
          >
            {t(`crm.act_${value}`)}
          </Button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          aria-label={t('crm.logPlaceholder')}
          placeholder={t('crm.logPlaceholder')}
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        <Button
          disabled={disabled || body.trim().length === 0}
          title={disabled ? t('crm.noActivityPermission') : undefined}
          onClick={() => {
            onSubmit(kind, body.trim());
            setBody('');
          }}
        >
          {t('crm.addToLog')}
        </Button>
      </div>
    </div>
  );
}

function DisqualifyModal({
  lead,
  onClose,
  onConfirm,
}: {
  lead: LeadResponse;
  onClose: () => void;
  onConfirm: (reason: LeadDisqualifyReason, note: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<'' | LeadDisqualifyReason>('');
  const [note, setNote] = useState('');
  return (
    <Modal title={t('crm.disqualifyTitle')} onClose={onClose}>
      <div className="grid gap-3">
        <p className="text-sm text-muted-foreground">
          {lead.name}
          {lead.company ? ` · ${lead.company}` : ''}
        </p>
        <div className="grid gap-1">
          <Label htmlFor="dq-reason">{t('crm.dqReason')}</Label>
          <NativeSelect
            id="dq-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value as '' | LeadDisqualifyReason)}
          >
            <option value="">{t('crm.chooseReason')}</option>
            {LEAD_DISQUALIFY_REASONS.map((value) => (
              <option key={value} value={value}>
                {t(REASON_KEYS[value])}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="grid gap-1">
          <Label htmlFor="dq-note">{t('crm.dqNote')}</Label>
          <Textarea id="dq-note" value={note} onChange={(event) => setNote(event.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {/* BR-CRM-01-10: disabled until a reason is chosen. */}
          <Button
            variant="destructive"
            disabled={reason === ''}
            onClick={() => onConfirm(reason as LeadDisqualifyReason, note.trim())}
          >
            {t('crm.disqualify')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
